import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  claimPutawayDirective,
  executePutawayMove,
  lookupLocationByCode,
  listWorkerPutawayQueue,
  releasePutawayClaim,
  suggestPutawayLocations,
  splitPutawayDirective,
} from "@/lib/putaway-directives.functions";
import { RfShell, RfEmpty, RfScopeMissing } from "@/components/rf/rf-shell";
import { ScanInput } from "@/components/ui/scan-input";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useScope } from "@/hooks/use-scope";
import { toast } from "sonner";
import { MapPin, Hand, Lightbulb } from "lucide-react";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/rf/putaway")({
  component: RfPutaway,
});

function RfPutaway() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listWorkerPutawayQueue);
  const claimFn = useServerFn(claimPutawayDirective);
  const releaseFn = useServerFn(releasePutawayClaim);
  const moveFn = useServerFn(executePutawayMove);
  const lookupFn = useServerFn(lookupLocationByCode);
  const suggestFn = useServerFn(suggestPutawayLocations);
  const splitFn = useServerFn(splitPutawayDirective);

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ["rf-putaway", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
    refetchInterval: 15_000,
  });

  const [activeId, setActiveId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [putQty, setPutQty] = useState<string>("");
  const [lastPlacement, setLastPlacement] = useState<
    { code: string; qty: number; remaining: number } | null
  >(null);
  const pendingSiblingRef = useRef<string | null>(null);
  const pendingRemainderRef = useRef<number | null>(null);
  const active: any = useMemo(
    () => (rows as any[]).find((r) => r.id === activeId) ?? null,
    [rows, activeId],
  );

  // Reset the qty input when switching directives — defaults to full.
  useEffect(() => {
    if (active) setPutQty(String(active.qty));
    else setPutQty("");
  }, [activeId, active?.qty]);

  const { data: suggestions = [] } = useQuery({
    queryKey: ["rf-putaway-suggest", activeId],
    enabled: !!activeId,
    queryFn: () => suggestFn({ data: { directiveId: activeId! } }),
    staleTime: 30_000,
  });

  if (!tenantId || !facilityId) {
    return (
      <RfShell title="Putaway" back>
        <RfScopeMissing />
      </RfShell>
    );
  }

  async function start(id: string) {
    const row: any = (rows as any[]).find((r) => r.id === id);
    setLastPlacement(null);
    if (row?.mine) { setActiveId(id); return; }
    try {
      await claimFn({ data: { id } });
      setActiveId(id);
      await refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Already claimed");
      await refetch();
    }
  }

  /**
   * Resolve the qty the worker wants to put away NOW. If it's less than the
   * full directive qty, split first so the remainder becomes its own open
   * directive (the worker can do it next, or another worker can pick it up).
   * Returns the directive id to execute the move against, or null on error.
   */
  async function ensureSplit(): Promise<string | null> {
    if (!active) return null;
    const parsed = Math.floor(Number(putQty));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error("Enter a qty greater than 0");
      return null;
    }
    if (parsed > active.qty) {
      toast.error(`Only ${active.qty} available to put away`);
      return null;
    }
    if (parsed === active.qty) return active.id;
    try {
      const res: any = await splitFn({ data: { id: active.id, partialQty: parsed } });
      // Remember the remainder sibling so we can hop into it after the move
      // and let the worker continue the same SKU without bouncing to the list.
      pendingSiblingRef.current = res?.siblingId ?? null;
      pendingRemainderRef.current = res?.remainderQty ?? null;
      return active.id;
    } catch (e: any) {
      toast.error(e?.message ?? "Split failed");
      return null;
    }
  }

  async function release() {
    if (!active) return;
    setBusy(true);
    try {
      await releaseFn({ data: { id: active.id } });
      setActiveId(null);
      await refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Release failed");
    } finally { setBusy(false); }
  }

  async function finishMove(destId: string, destCode: string, movedQty: number) {
    const siblingId = pendingSiblingRef.current;
    const remainder = pendingRemainderRef.current ?? 0;
    pendingSiblingRef.current = null;
    pendingRemainderRef.current = null;
    setLastPlacement({ code: destCode, qty: movedQty, remaining: remainder });
    if (siblingId && remainder > 0) {
      // Continue the same SKU on the same screen — claim the remainder and
      // stay put so the worker can scan the next destination immediately.
      try { await claimFn({ data: { id: siblingId } }); } catch { /* still surface in list */ }
      await refetch();
      setActiveId(siblingId);
    } else {
      // Fully put away — return to the queue.
      await refetch();
      setActiveId(null);
    }
    qc.invalidateQueries({ queryKey: ["rf-putaway"] });
  }

  async function handleScan(code: string) {
    if (!active) return;
    setBusy(true);
    try {
      const loc: any = await lookupFn({
        data: { tenantId: tenantId!, facilityId: facilityId!, code: code.trim() },
      });
      if (!loc) { toast.error(`Unknown location "${code}"`); return; }
      const movedQty = Math.floor(Number(putQty)) || active.qty;
      const targetId = await ensureSplit();
      if (!targetId) return;
      await moveFn({
        data: {
          id: targetId,
          destinationLocationId: loc.id,
          idempotencyKey: `rf-putaway-${active.id}-${Date.now()}`,
        },
      });
      toast.success(`Putaway → ${loc.code}`);
      await finishMove(loc.id, loc.code, movedQty);
    } catch (e: any) {
      toast.error(e?.message ?? "Move failed");
    } finally {
      setBusy(false);
    }
  }

  async function pickSuggestion(s: any) {
    if (!active || busy) return;
    setBusy(true);
    try {
      const movedQty = Math.floor(Number(putQty)) || active.qty;
      const targetId = await ensureSplit();
      if (!targetId) return;
      await moveFn({
        data: {
          id: targetId,
          destinationLocationId: s.location_id,
          idempotencyKey: `rf-putaway-${active.id}-${Date.now()}`,
        },
      });
      toast.success(`Putaway → ${s.code}`);
      await finishMove(s.location_id, s.code, movedQty);
    } catch (e: any) {
      toast.error(e?.message ?? "Move failed");
    } finally {
      setBusy(false);
    }
  }

  if (active) {
    const parsedQty = Math.floor(Number(putQty));
    const isPartial = Number.isFinite(parsedQty) && parsedQty > 0 && parsedQty < active.qty;
    return (
      <RfShell title="Putaway" back="/app/rf/putaway">
        {lastPlacement ? (
          <div className="rounded-md border border-success/40 bg-success/10 p-3 flex items-start gap-2">
            <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
            <div className="text-sm min-w-0">
              <div className="font-medium">
                Put away {lastPlacement.qty} → <span className="font-mono">{lastPlacement.code}</span>
              </div>
              {lastPlacement.remaining > 0 ? (
                <div className="text-xs text-muted-foreground">
                  {lastPlacement.remaining} remaining — scan the next destination.
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="rounded-md border bg-muted/30 p-3 space-y-1">
          <div className="font-mono text-sm">{active.sku?.sku_code ?? "SKU"}</div>
          <div className="text-sm">{active.sku?.name ?? ""}</div>
          <div className="text-xs text-muted-foreground">Qty {active.qty}</div>
          {active.suggested_location?.code ? (
            <div className="text-xs text-success mt-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> Suggested {active.suggested_location.code}
            </div>
          ) : null}
        </div>
        <div className="rounded-md border bg-card p-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Put away qty
            </label>
            <button
              type="button"
              onClick={() => setPutQty(String(active.qty))}
              className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
              disabled={busy}
            >
              All ({active.qty})
            </button>
          </div>
          <Input
            inputMode="numeric"
            pattern="[0-9]*"
            value={putQty}
            onChange={(e) => setPutQty(e.target.value.replace(/[^0-9]/g, ""))}
            disabled={busy}
            className="h-11 text-lg tabular-nums"
          />
          {isPartial ? (
            <div className="text-[11px] text-muted-foreground">
              Remaining {active.qty - parsedQty} will stay as an open directive for the next location.
            </div>
          ) : null}
        </div>
        <ScanInput onScan={handleScan} placeholder="Scan destination bin…" size="lg" disabled={busy} />
        {(suggestions as any[]).length > 0 ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1 px-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              <Lightbulb className="h-3 w-3" /> Suggested locations
            </div>
            <div className="space-y-1.5">
              {(suggestions as any[]).map((s) => (
                <button
                  key={s.location_id}
                  type="button"
                  disabled={busy}
                  onClick={() => pickSuggestion(s)}
                  className="flex w-full items-center justify-between gap-2 rounded-md border bg-card px-3 py-2.5 text-left active:bg-muted/70 disabled:opacity-50"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-sm truncate flex items-center gap-1.5">
                      {s.code}
                      {s.type ? (
                        <span className="rounded bg-muted px-1 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">
                          {s.type}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {s.reason === "rule"
                        ? "Rule"
                        : s.reason === "consolidate"
                          ? `Already here · ${s.qty_on_hand} on hand`
                          : s.reason === "reserve"
                            ? "Reserve / bulk storage"
                            : s.role
                              ? `Planogram · ${s.role}`
                              : "Planogram"}
                      {s.zone ? ` · ${s.zone}` : ""}
                    </div>
                  </div>
                  <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
            <div className="px-1 text-[11px] text-muted-foreground">
              Or scan a different bin above.
            </div>
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={release} disabled={busy}>Release</Button>
          <Button variant="outline" onClick={() => setActiveId(null)} disabled={busy}>Back</Button>
        </div>
      </RfShell>
    );
  }

  return (
    <RfShell title="Putaway" back>
      <div className="text-xs text-muted-foreground px-1">
        {isLoading ? "Loading…" : `${rows.length} open directives`}
      </div>
      <div className="space-y-2">
        {rows.length === 0 && !isLoading ? (
          <RfEmpty>No open putaway tasks.</RfEmpty>
        ) : (
          (rows as any[]).map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => start(r.id)}
              className="flex w-full items-center gap-3 rounded-md border bg-card px-3 py-3 text-left active:bg-muted/70"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-sm">{r.sku?.sku_code ?? "—"}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {r.source_location?.code ? `From ${r.source_location.code}` : ""}
                  {r.suggested_location?.code ? ` → ${r.suggested_location.code}` : ""}
                </div>
                {r.mine ? (
                  <div className="text-[11px] text-emerald-600 flex items-center gap-1 mt-0.5">
                    <Hand className="h-3 w-3" /> yours · resume
                  </div>
                ) : null}
              </div>
              <div className="shrink-0 text-right">
                <div className="text-base font-semibold tabular-nums">{r.qty}</div>
                <div className="text-[10px] text-muted-foreground">qty</div>
              </div>
            </button>
          ))
        )}
      </div>
    </RfShell>
  );
}